import { useState, useEffect } from 'react';
import { Loader2, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUpdateSource } from '@/features/sources/hooks/useSources';
import type { Source } from '@/types/database';

interface SourceEditDialogProps {
  source: Source | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SourceEditDialog({ source, open, onOpenChange }: SourceEditDialogProps) {
  const updateSource = useUpdateSource();

  const [title, setTitle] = useState('');
  const [authors, setAuthors] = useState<string[]>([]);
  const [authorInput, setAuthorInput] = useState('');
  const [publicationYear, setPublicationYear] = useState('');
  const [journal, setJournal] = useState('');
  const [doi, setDoi] = useState('');
  const [abstract, setAbstract] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');

  // Metadata JSONB fields
  const [volume, setVolume] = useState('');
  const [issue, setIssue] = useState('');
  const [pages, setPages] = useState('');
  const [publisher, setPublisher] = useState('');
  const [url, setUrl] = useState('');
  const [language, setLanguage] = useState('');

  useEffect(() => {
    if (source) {
      const meta = (source.metadata ?? {}) as Record<string, unknown>;
      setTitle(source.title ?? '');
      setAuthors(source.authors ?? []);
      setAuthorInput('');
      setPublicationYear(source.publication_year?.toString() ?? '');
      setJournal(source.journal ?? '');
      setDoi(source.doi ?? '');
      setAbstract(source.abstract ?? '');
      setKeywords(source.keywords ?? []);
      setKeywordInput('');
      setVolume((meta.volume as string) ?? '');
      setIssue((meta.issue as string) ?? '');
      setPages((meta.pages as string) ?? '');
      setPublisher((meta.publisher as string) ?? '');
      setUrl((meta.url as string) ?? '');
      setLanguage((meta.language as string) ?? '');
    }
  }, [source]);

  const addAuthor = () => {
    const trimmed = authorInput.trim();
    if (trimmed && !authors.includes(trimmed)) {
      setAuthors([...authors, trimmed]);
      setAuthorInput('');
    }
  };

  const removeAuthor = (index: number) => {
    setAuthors(authors.filter((_, i) => i !== index));
  };

  const addKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !keywords.includes(trimmed)) {
      setKeywords([...keywords, trimmed]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!source) return;

    const existingMeta = (source.metadata ?? {}) as Record<string, unknown>;
    const mergedMetadata = {
      ...existingMeta,
      volume: volume || undefined,
      issue: issue || undefined,
      pages: pages || undefined,
      publisher: publisher || undefined,
      url: url || undefined,
      language: language || undefined,
    };

    await updateSource.mutateAsync({
      id: source.id,
      updates: {
        title: title.trim() || null,
        authors,
        publication_year: publicationYear ? parseInt(publicationYear, 10) : null,
        journal: journal.trim() || null,
        doi: doi.trim() || null,
        abstract: abstract.trim() || null,
        keywords,
      },
    });

    // Update metadata JSONB separately via direct supabase call
    const { supabase } = await import('@/lib/supabase');
    await supabase
      .from('sources')
      .update({ metadata: mergedMetadata })
      .eq('id', source.id);

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Metadata</DialogTitle>
          <DialogDescription>
            Update bibliographic information for this source.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-title">Title</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Source title"
            />
          </div>

          {/* Authors */}
          <div className="space-y-1.5">
            <Label>Authors</Label>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {authors.map((author, i) => (
                <Badge key={i} variant="secondary" className="gap-1 pr-1">
                  {author}
                  <button
                    type="button"
                    onClick={() => removeAuthor(i)}
                    className="ml-0.5 rounded-full hover:bg-slate-300 p-0.5"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={authorInput}
                onChange={(e) => setAuthorInput(e.target.value)}
                placeholder="Add author name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); addAuthor(); }
                }}
                className="flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={addAuthor}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Year + Journal row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-year">Publication Year</Label>
              <Input
                id="edit-year"
                type="number"
                value={publicationYear}
                onChange={(e) => setPublicationYear(e.target.value)}
                placeholder="2024"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-journal">Journal</Label>
              <Input
                id="edit-journal"
                value={journal}
                onChange={(e) => setJournal(e.target.value)}
                placeholder="Nature"
              />
            </div>
          </div>

          {/* DOI */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-doi">DOI</Label>
            <Input
              id="edit-doi"
              value={doi}
              onChange={(e) => setDoi(e.target.value)}
              placeholder="10.1000/xyz123"
            />
          </div>

          {/* Abstract */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-abstract">Abstract</Label>
            <Textarea
              id="edit-abstract"
              value={abstract}
              onChange={(e) => setAbstract(e.target.value)}
              placeholder="Paper abstract..."
              rows={4}
            />
          </div>

          {/* Keywords */}
          <div className="space-y-1.5">
            <Label>Keywords</Label>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {keywords.map((kw, i) => (
                <Badge key={i} variant="secondary" className="gap-1 pr-1">
                  {kw}
                  <button
                    type="button"
                    onClick={() => removeKeyword(i)}
                    className="ml-0.5 rounded-full hover:bg-slate-300 p-0.5"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                placeholder="Add keyword"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); addKeyword(); }
                }}
                className="flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={addKeyword}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Volume, Issue, Pages */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-volume">Volume</Label>
              <Input
                id="edit-volume"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-issue">Issue</Label>
              <Input
                id="edit-issue"
                value={issue}
                onChange={(e) => setIssue(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-pages">Pages</Label>
              <Input
                id="edit-pages"
                value={pages}
                onChange={(e) => setPages(e.target.value)}
                placeholder="1-15"
              />
            </div>
          </div>

          {/* Publisher, URL, Language */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-publisher">Publisher</Label>
              <Input
                id="edit-publisher"
                value={publisher}
                onChange={(e) => setPublisher(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-language">Language</Label>
              <Input
                id="edit-language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="English"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-url">URL</Label>
            <Input
              id="edit-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateSource.isPending}>
            {updateSource.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
